import { Mutex } from 'async-mutex';
import { useSetAtom } from 'jotai';
import { useCallback } from 'react';

import { useUpdateCachedReflectionTopicCompletionStatusCallback } from '../../hooks/reflections/useUpdateCachedReflectionTopicCompletionStatusCallback';
import { disarmNavigationGuard } from '../../session/hooks/useNavigationGuard';
import { useHandleWhiteboardUpdateCallback } from '../../session/messages/useHandleWhiteboardUpdateMessage';
import { DataChannelMessage, DataChannelMessageType } from '../../video-sdk';
import { isRestartingSessionAtom } from '../state';
import { useLoadWhiteboardStoreSnapshotCallback } from './useLoadWhiteboardStoreSnapshotCallback';
import { useUpdateDataChannelAppStateCallback } from './useUpdateDataChannelAppStateCallback';

const handleReceivedMessageMutex = new Mutex();

/**
 * Returns a callback that handles received messages on the data track for the
 * client
 */
export function useHandleReceivedMessageCallback() {
  const updateDataTrackState = useUpdateDataChannelAppStateCallback();
  const updateWhiteboard = useHandleWhiteboardUpdateCallback();
  const loadWhiteboardStoreSnapshot = useLoadWhiteboardStoreSnapshotCallback();
  const updateCachedTopicCompletionStatus = useUpdateCachedReflectionTopicCompletionStatusCallback();
  const setIsRestartingSession = useSetAtom(isRestartingSessionAtom);

  return useCallback(
    (message: DataChannelMessage) => {
      return handleReceivedMessageMutex.runExclusive(async () => {
        switch (message.type) {
          case DataChannelMessageType.APP_STATE:
            updateDataTrackState(message);
            break;
          case DataChannelMessageType.WHITEBOARD_UPDATE:
            await updateWhiteboard(message);
            break;
          case DataChannelMessageType.WHITEBOARD_SNAPSHOT:
            await loadWhiteboardStoreSnapshot(message);
            break;
          case DataChannelMessageType.REFLECTION_TOPIC_UPDATE:
            updateCachedTopicCompletionStatus(message.topic);
            break;
          case DataChannelMessageType.RESTART_SESSION:
            setIsRestartingSession(true);
            disarmNavigationGuard();
            window.location.reload();
            break;
        }
      });
    },
    [
      loadWhiteboardStoreSnapshot,
      setIsRestartingSession,
      updateCachedTopicCompletionStatus,
      updateDataTrackState,
      updateWhiteboard,
    ],
  );
}
